/**
 * CoordEditPanel.tsx — Modal de edición manual de coordenadas UTM30 (Lote C1)
 *
 * Entrada designada: botón «Edición manual» del faldón de InlineMapPanel
 * (callback onEditManual). Envuelve el componente probado CoordinateFields,
 * que acepta cualquier formato (DMS / DD / UTM), autodetecta, convierte a
 * UTM30 (EPSG:25830) y expone X/Y editables con validación en vivo.
 *
 * Fuente única de verdad: ProcessedInfrastructure.xFinal / yFinal. Al aplicar,
 * llama onApply(infraId, x, y) → WizardPageV2 patchResult(id, { xFinal, yFinal }).
 * Ese mismo par alimentará el pin arrastrable de C1b (β) sin reescritura.
 *
 * Diseño: overlay + tarjeta DS v2 (calcado de InlineEditForm), role="dialog",
 * aria-modal, Escape cancela, Ctrl/Cmd+Enter aplica.
 *
 * @module wizard-v2/step2-valida/CoordEditPanel
 */

import {
  useState,
  type FC,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';
import { colors, typography, borderRadius, transitions } from '@/design-system/v2';
import { CoordinateFields } from '@/components/Step2/CoordinateFields';

// ============================================================================
// TYPES
// ============================================================================

export interface CoordEditPanelProps {
  /** ID de la infraestructura que se está editando */
  infraId: string;
  /** Nombre de la infraestructura (cabecera del modal) */
  infraName?: string;
  /** Coordenada X (Este) actual en UTM30 — ProcessedInfrastructure.xFinal */
  currentX: number | null;
  /** Coordenada Y (Norte) actual en UTM30 — ProcessedInfrastructure.yFinal */
  currentY: number | null;
  /** Callback al aplicar — recibe las coordenadas UTM30 confirmadas */
  onApply: (infraId: string, x: number, y: number) => void;
  /** Callback al cancelar — cierra el modal sin guardar */
  onCancel: () => void;
}

// ============================================================================
// STYLES (tokens DS v2, calcado de InlineEditForm)
// ============================================================================

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.65)',
  zIndex: 9000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
};

const cardStyle: CSSProperties = {
  // VIS-2b-ii (Bucket A, DOP-BUCKETA=a): hex huérfano casi-duplicado (d=6) → bg.secondary, superficie elevada del DS
  background: colors.bg.secondary,
  border: `1px solid ${colors.border.DEFAULT}`,
  borderRadius: borderRadius.xl,
  padding: '28px 32px',
  width: '100%',
  maxWidth: '560px',
  maxHeight: '90vh',
  overflowY: 'auto',
  boxShadow: '0 24px 64px rgba(0,0,0,.55)',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  fontFamily: typography.fontFamily.sans,
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '12px',
};

const titleWrapStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  minWidth: 0,
};

const titleStyle: CSSProperties = {
  fontSize: typography.fontSize.lg,
  fontWeight: typography.fontWeight.semibold,
  color: colors.fg.primary,
  margin: 0,
};

const subtitleStyle: CSSProperties = {
  fontSize: typography.fontSize.xs,
  color: colors.fg.muted,
  margin: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const closeBtnStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: colors.fg.muted,
  padding: '4px',
  display: 'flex',
  alignItems: 'center',
  borderRadius: borderRadius.sm,
  transition: transitions.base,
  flexShrink: 0,
};

const hintStyle: CSSProperties = {
  fontSize: '11px',
  color: colors.fg.subtle,
  margin: 0,
};

const kbdStyle: CSSProperties = {
  fontFamily: typography.fontFamily.mono,
  fontSize: '10px',
  padding: '1px 4px',
  background: colors.bg.tertiary,
  borderRadius: '3px',
  border: `1px solid ${colors.border.DEFAULT}`,
};

const footerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '10px',
  marginTop: '4px',
};

const btnCancelStyle: CSSProperties = {
  padding: '9px 18px',
  borderRadius: borderRadius.md,
  border: `1px solid ${colors.border.DEFAULT}`,
  background: 'transparent',
  color: colors.fg.muted,
  fontFamily: typography.fontFamily.sans,
  fontSize: typography.fontSize.sm,
  fontWeight: typography.fontWeight.medium,
  cursor: 'pointer',
  transition: transitions.base,
};

const btnApplyStyle: CSSProperties = {
  padding: '9px 20px',
  borderRadius: borderRadius.md,
  border: 'none',
  background: colors.primary,
  color: '#18181b',
  fontFamily: typography.fontFamily.sans,
  fontSize: typography.fontSize.sm,
  fontWeight: typography.fontWeight.semibold,
  cursor: 'pointer',
  transition: transitions.base,
};

// ============================================================================
// ICON
// ============================================================================

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} stroke="currentColor" strokeWidth={2} fill="none" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

// ============================================================================
// COMPONENT
// ============================================================================

export const CoordEditPanel: FC<CoordEditPanelProps> = ({
  infraId,
  infraName,
  currentX,
  currentY,
  onApply,
  onCancel,
}) => {
  const [x, setX] = useState<number | null>(currentX);
  const [y, setY] = useState<number | null>(currentY);

  const isValid =
    typeof x === 'number' && Number.isFinite(x) &&
    typeof y === 'number' && Number.isFinite(y);

  const hasChanges = x !== currentX || y !== currentY;
  const canApply = isValid && hasChanges;

  const handleApply = () => {
    if (!canApply) return;
    onApply(infraId, x as number, y as number);
  };

  const handleOverlayKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
  };

  const handleCardKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleApply();
    }
  };

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  return (
    <div
      style={overlayStyle}
      onClick={onCancel}
      onKeyDown={handleOverlayKeyDown}
      role="presentation"
      data-testid="coord-edit-overlay"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="coord-edit-title"
        style={cardStyle}
        onClick={handleCardClick}
        onKeyDown={handleCardKeyDown}
        data-testid="coord-edit-panel"
      >
        {/* Cabecera */}
        <div style={headerStyle}>
          <div style={titleWrapStyle}>
            <h2 id="coord-edit-title" style={titleStyle}>
              Editar coordenadas
            </h2>
            {infraName && (
              <p style={subtitleStyle} title={infraName}>{infraName}</p>
            )}
          </div>
          <button
            type="button"
            style={closeBtnStyle}
            onClick={onCancel}
            aria-label="Cerrar editor de coordenadas"
            title="Cancelar (Escape)"
          >
            <XIcon />
          </button>
        </div>

        {/* Cuerpo: campos de coordenadas (pega cualquier formato → UTM30) */}
        <CoordinateFields
          theme="dark"
          currentX={x}
          currentY={y}
          onCoordinatesChange={(nx, ny) => { setX(nx); setY(ny); }}
        />

        {/* Hint teclado */}
        <p style={hintStyle}>
          <kbd style={kbdStyle}>Ctrl</kbd>{' + '}
          <kbd style={kbdStyle}>Enter</kbd>{' para aplicar · '}
          <kbd style={kbdStyle}>Esc</kbd>{' para cancelar'}
        </p>

        {/* Footer */}
        <div style={footerStyle}>
          <button
            type="button"
            style={btnCancelStyle}
            onClick={onCancel}
            data-testid="coord-edit-cancel"
          >
            Cancelar
          </button>
          <button
            type="button"
            style={canApply ? btnApplyStyle : { ...btnApplyStyle, opacity: 0.4, cursor: 'not-allowed' }}
            onClick={handleApply}
            disabled={!canApply}
            data-testid="coord-edit-apply"
            title={!isValid ? 'Introduce coordenadas válidas' : (!hasChanges ? 'Sin cambios' : 'Aplicar coordenadas')}
          >
            Aplicar
          </button>
        </div>
      </div>
    </div>
  );
};

export default CoordEditPanel;
